import { useState } from "react";
import { VISOR_MODULE_BREADCRUMB_ROOT } from "@/modules/workModuleCore";
import { PageHeader } from "./layoutChrome";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { BookOpen, Wrench, Download, ExternalLink } from "lucide-react";
import { API } from "@/lib/api";

/**
 * Pestaña de Ayuda con dos manuales en PDF:
 * - Manual de uso del sistema (cómo operar día a día)
 * - Manual de instalación (requisitos, activación, actualizaciones)
 *
 * Los PDFs se generan server-side con reportlab y se sirven desde:
 *   GET /api/docs/manual-uso
 *   GET /api/docs/manual-instalacion
 */
export default function Ayuda() {
  const [tab, setTab] = useState("uso");

  const urlUso = `${API}/docs/manual-uso`;
  const urlInst = `${API}/docs/manual-instalacion`;

  return (
    <div className="min-h-screen">
      <PageHeader
        title="Ayuda y Manuales"
        subtitle="Documentación del sistema — uso cotidiano y guía de instalación."
        breadcrumb={[VISOR_MODULE_BREADCRUMB_ROOT, "Ayuda"]}
      />

      <div className="p-8 space-y-4">
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList>
            <TabsTrigger value="uso" data-testid="tab-manual-uso">
              <BookOpen className="w-4 h-4 mr-1.5" /> Manual de uso del sistema
            </TabsTrigger>
            <TabsTrigger value="instalacion" data-testid="tab-manual-instalacion">
              <Wrench className="w-4 h-4 mr-1.5" /> Manual de instalación
            </TabsTrigger>
          </TabsList>

          <TabsContent value="uso" className="mt-4">
            <ManualPanel
              title="Manual de uso del sistema"
              description="Guía paso a paso (v2.15): cliente de trabajo, confronta CFDI, Asistente Laboral/REPSE (SISUB/ICSOE), EFOS y licencias."
              url={urlUso}
              filename="Manual_Uso_VisorFiscal.pdf"
              testId="frame-manual-uso"
            />
          </TabsContent>

          <TabsContent value="instalacion" className="mt-4">
            <ManualPanel
              title="Manual de instalación"
              description="Requisitos del sistema, instalación en Windows, activación de licencia, backups automáticos y cómo actualizar sin perder datos."
              url={urlInst}
              filename="Manual_Instalacion_VisorFiscal.pdf"
              testId="frame-manual-instalacion"
            />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

function ManualPanel({ title, description, url, filename, testId }) {
  return (
    <div className="bg-white rounded-md border border-slate-200 overflow-hidden">
      <div className="flex items-start justify-between gap-4 p-4 border-b border-slate-200 bg-slate-50">
        <div>
          <h3 className="font-semibold text-slate-900 text-sm">{title}</h3>
          <p className="text-xs text-slate-500 mt-1 max-w-2xl">{description}</p>
        </div>
        <div className="flex gap-2 flex-shrink-0">
          <Button
            variant="outline"
            onClick={() => {
              const a = document.createElement("a");
              a.href = url;
              a.download = filename;
              a.click();
            }}
            className="h-9 text-xs"
            data-testid={`${testId}-download-btn`}
          >
            <Download className="w-3.5 h-3.5 mr-1.5" /> Descargar PDF
          </Button>
          <Button
            variant="outline"
            onClick={() => window.open(url, "_blank")}
            className="h-9 text-xs"
            data-testid={`${testId}-open-btn`}
          >
            <ExternalLink className="w-3.5 h-3.5 mr-1.5" /> Abrir en pestaña nueva
          </Button>
        </div>
      </div>
      <iframe
        title={title}
        src={url}
        className="w-full h-[calc(100vh-260px)] border-0"
        data-testid={testId}
      />
    </div>
  );
}
